import { Fragment, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { ROLE_LABELS, billedAsSelf, grantsOf } from "../auth/roles";
import { usePortalConfig } from "../portalConfig";
import {
  Btn, Card, Field, Input, PasswordInput, PasswordRequirements, PasswordStrength,
  passwordIsAcceptable,
} from "../components/Shared";

/**
 * Your own account, editable — the door a non-operator can walk through.
 *
 * Personal Administration printed your name and address and offered no way to
 * change either. Editing an account happened in exactly one place,
 * `PATCH /api/admin/users/{id}`, which is operator-gated, so an admin, group
 * admin or operator edited themselves through a screen about other people and a
 * plain user could change **nothing** about their own account: not a name
 * misspelled at account creation, not an address that had changed, and not
 * their password, because there was no change-password route at all — the only
 * route to a new password was the forgotten-password flow, which needs an
 * address on file and mail working.
 *
 * Three rules are visible on this card and each is the server's, not this
 * file's; the card renders them and `PATCH /api/me` enforces them.
 *
 *   * **Only what was touched is posted.** The admin form posts the whole
 *     object it was opened with, which is why the backend's self-edit guard had
 *     to become a change-test rather than a presence-test. A card that sends
 *     only dirty fields does not need that, and copying the old shape without
 *     copying the reason is how it would come back.
 *   * **Changing the address or the password asks for the current
 *     password.** The address is the account-recovery channel and the password
 *     is the credential. A session left open on a shared machine has no
 *     authority over either.
 *   * **A Keycloak account's address and password are Keycloak's.** The address
 *     is the identity the account was linked by and is refreshed at every
 *     sign-in, so the card shows it read-only and says where it is changed;
 *     there is no portal password, so none is offered and none is asked for.
 *
 * No PPMS login and no Sync with PPMS since 2026-10-20: the per-person sync
 * gave way to the PPMS catalogue on the Projects page, where any PPMS project
 * is found by name and imported, so an account no longer names a PPMS user.
 *
 * It issues **no request on mount** — everything on it is already on the
 * `/auth/me` object `AuthContext` holds.
 */

const NOTE = {
  marginBottom: 14, padding: "10px 12px", borderRadius: 8, fontSize: 13,
  background: "#f3f4f6", color: "#374151",
};

// The six lines of a billing identity, in `accounts.BILLING_FROM_GROUP`'s order,
// with the label each wears. Exported with the block below because the account
// form in Management → Users shows the same block, and two copies of six labels
// are how the profile and the form come to disagree.
export const BILLING_LINES = [
  ["billing_name",    "Institution or company"],
  ["billing_street",  "Street"],
  ["billing_zip",     "Postcode"],
  ["billing_city",    "City"],
  ["billing_country", "Country"],
  ["vat_id",          "UID / VAT ID"],
];

/**
 * An operator's or group admin's billing identity, which is its GROUP's — the
 * legal name, address and VAT id from Facility settings, read by the server at
 * the moment `/auth/me` or `/api/admin/users` answered (`billing_identity`,
 * 2026-09-28). Read-only everywhere: `PATCH /api/admin/users/{id}` refuses a
 * changed value for a staff account, and the place to change it is the group.
 *
 * `account` null means the values are not known yet — the admin form after the
 * role or group was changed, or on create — and the block says whose they will
 * be instead of showing another account's. An administrator has no billing
 * identity and callers render nothing for one.
 */
export function GroupBillingBlock({ account, groupName }) {
  const headingId = `group-billing-${account?.id ?? "new"}`;
  return (
    <section aria-labelledby={headingId}>
      <h4 id={headingId} style={{ margin: "0 0 2px", fontSize: 12, fontWeight: 600, color: "#374151",
                                  letterSpacing: "0.05em", textTransform: "uppercase" }}>
        Billed as the group
      </h4>
      <p style={{ margin: "0 0 10px", fontSize: 11, color: "#6b7280" }}>
        {groupName ? `from group ${groupName}` : "from the account's group"} — an operator or
        group admin is billed as their group. Change it in Management → Facility settings.
      </p>
      {account && (
        <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", fontSize: 13 }}>
          {BILLING_LINES.map(([key, label]) => (
            <Fragment key={key}>
              <dt style={{ color: "#6b7280" }}>{label}</dt>
              <dd style={{ margin: 0 }}>{account[key] || "(none recorded)"}</dd>
            </Fragment>
          ))}
        </dl>
      )}
    </section>
  );
}

/** Whether `role` is billed as its group: operator and group admin only. */
export const billedAsGroup = role => role === "operator" || role === "group_admin";

// The six keys a person states about themselves while their account is not yet
// confirmed. VAT is the one that is optional (the maintainer, 2026-09-28).
const BILLING_KEYS = BILLING_LINES.map(([key]) => key);
const OPTIONAL_BILLING = new Set(["vat_id"]);

/**
 * What the account's state says, in one sentence, or null for a confirmed one.
 * The server's own words for the gate (`deps._assert_profile_confirmed`) are
 * the longer form; this is what the person reads before they hit it.
 */
function profileStateSentence(state) {
  if (state === "incomplete") {
    return "Complete your details — your name, e-mail address and the full billing address — "
         + "and submit them. Until the facility confirms them, your account can reach only "
         + "this profile.";
  }
  if (state === "submitted") {
    return "Your details are with the facility, waiting for the facility to confirm them. "
         + "You can still correct them here until then.";
  }
  return null;
}

export default function AccountCard() {
  const { user, refreshUser } = useAuth();
  const { oidc_display_name: oidcName } = usePortalConfig();

  // A Keycloak account's password and second factor are Keycloak's: the portal
  // refuses a password sign-in for it, so a password set here would be one
  // nothing ever asks for. The card says where they are kept instead.
  const isKeycloak = user?.auth_source === "keycloak";
  const idpName = oidcName || "Keycloak";
  // **A self-registered account states its own billing identity until an
  // operator confirms it** (2026-09-28): `PATCH /api/me` takes the six billing
  // keys only while `profile_state` is not `confirmed`, and only for a `user`
  // (staff are billed as their group). After confirmation they are staff's to
  // edit, and this card offers no box for them — the rule the server keeps.
  const unconfirmed = !!user?.profile_state && user.profile_state !== "confirmed";
  const ownBilling = unconfirmed && billedAsSelf(user?.role);

  const [form, setForm] = useState({
    // Two halves since 2026-09-16; `full_name` is composed by the server as
    // "surname given-name" and `PATCH /api/me` refuses it outright, so sending
    // it would be a 422 naming a field this card has no box for.
    first_name: user?.first_name ?? "",
    last_name: user?.last_name ?? "",
    email: user?.email ?? "",
    ...Object.fromEntries(BILLING_KEYS.map(key => [key, user?.[key] ?? ""])),
    password: "",
    current_password: "",
  });
  const [error, setError]   = useState(null);
  const [saved, setSaved]   = useState(false);
  const [busy, setBusy]     = useState(false);

  const set = k => e => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    setSaved(false);
  };

  // What actually differs from the account as it stands. Both sides are
  // normalised the way the server normalises them — a blank field and a null
  // column are the same thing — or clearing an already-empty box would count
  // as a change and demand a password for nothing.
  const same = (a, b) => (a || "").trim() === (b || "");
  const dirty = {
    first_name: !same(form.first_name, user?.first_name),
    last_name:  !same(form.last_name, user?.last_name),
    email:      !isKeycloak && !same(form.email, user?.email),
    password:   !isKeycloak && !!form.password,
    ...Object.fromEntries(BILLING_KEYS.map(key => [key, ownBilling && !same(form[key], user?.[key])])),
  };
  const anyDirty  = Object.values(dirty).some(Boolean);
  // A Keycloak account has no portal password to confirm, so it is never
  // asked for one: its e-mail is read-only and its password is not offered.
  const needsAuth = !isKeycloak && (dirty.email || dirty.password);
  // Against the address being saved, not the one on file: a request may set a
  // new e-mail and a password in the same breath, and the server checks the new.
  const passwordOk = !form.password || passwordIsAcceptable(form.password, form.email);
  const canSave = anyDirty && passwordOk && !busy
                  && (!needsAuth || !!form.current_password);

  // Only the dirty keys, trimmed, and the current password when one is asked.
  const dirtyPayload = () => {
    const payload = {};
    for (const key of ["first_name", "last_name", "email", "password", ...BILLING_KEYS]) {
      if (dirty[key]) payload[key] = form[key].trim ? form[key].trim() : form[key];
    }
    if (needsAuth) payload.current_password = form.current_password;
    return payload;
  };

  const save = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.patch("/api/me", dirtyPayload());
      setForm(f => ({ ...f, password: "", current_password: "" }));
      setSaved(true);
      // The first screen where the actor and the target are the same account,
      // so the header, the nav and every other reader of `user` would otherwise
      // keep the old answer until a reload. Optional at the call site: every
      // useAuth mock in the tree is `{ user }`.
      refreshUser?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // `incomplete → submitted`. What was typed is saved FIRST: the server checks
  // completeness on what it holds, so submitting over unsaved boxes would be
  // refused for fields that are filled in on screen. A refusal is the server's
  // sentence, which names every missing field at once.
  const submitProfile = async () => {
    setBusy(true);
    setError(null);
    try {
      if (anyDirty) {
        await api.patch("/api/me", dirtyPayload());
        setForm(f => ({ ...f, password: "", current_password: "" }));
      }
      await api.post("/api/me/profile/submit");
      setSaved(false);
      refreshUser?.();
    } catch (err) {
      setError(err.message);
      // A save that went through before a refused submit is still a save.
      refreshUser?.();
    } finally {
      setBusy(false);
    }
  };

  const grants = grantsOf(user);

  return (
    <Card>
      <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Account</h3>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
        What the portal knows about you, and what you can change yourself.
      </p>

      {error && (
        <div role="alert" style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8,
                                   background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)",
                                   color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}
      {saved && !error && (
        <div role="status" style={NOTE}>Saved.</div>
      )}
      {unconfirmed && (
        <div role="status" style={{ ...NOTE, background: "#fef3c7", color: "#92400e" }}>
          {profileStateSentence(user.profile_state)}
        </div>
      )}

      {/* `noValidate` while unconfirmed: the required marks say what a SUBMIT
          needs, and must not stop the person saving half of it on the way. */}
      <form onSubmit={save} noValidate={unconfirmed}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
          <Field label="First name" required={unconfirmed}>
            <Input value={form.first_name} onChange={set("first_name")} placeholder="Marie" />
          </Field>
          <Field label="Surname" required={unconfirmed}>
            <Input value={form.last_name} onChange={set("last_name")} placeholder="Curie" />
          </Field>
          <Field label="E-mail address" required={unconfirmed}
                 hint="Where the portal writes to you, and where a forgotten-password link is sent.">
            <Input type="email" value={form.email} onChange={set("email")}
                   readOnly={isKeycloak} placeholder="you@boku.ac.at" />
            {isKeycloak && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#4b5563" }}>
                Your e-mail address is managed in {idpName}.{" "}
                {user?.account_console_url && (
                  <a href={user.account_console_url} target="_blank" rel="noopener noreferrer">
                    Change it there
                  </a>
                )}
              </div>
            )}
          </Field>
          {isKeycloak ? (
            <div style={{ ...NOTE, marginBottom: 0, alignSelf: "start" }}>
              Your password and second factor are managed in {idpName}.{" "}
              {user?.account_console_url && (
                <a href={user.account_console_url} target="_blank" rel="noopener noreferrer">
                  Open your {idpName} account
                </a>
              )}
            </div>
          ) : (
          <Field label="New password (leave blank to keep)">
            <PasswordInput value={form.password} onChange={set("password")}
                           placeholder="••••••••" />
            {form.password && <PasswordStrength password={form.password} />}
            {form.password &&
              <PasswordRequirements password={form.password} identity={form.email} />}
          </Field>
          )}
        </div>

        {/* The person's own billing identity, while it is still theirs to
            state. VAT sits in the address block, the one line of it that is
            optional — the same block, in the same order, as the account form
            in Management → Users. */}
        {ownBilling && (
          <section aria-labelledby="own-billing-heading">
            <h4 id="own-billing-heading"
                style={{ margin: "0 0 2px", fontSize: 12, fontWeight: 600, color: "#374151",
                         letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Billing address
            </h4>
            <p style={{ margin: "0 0 10px", fontSize: 11, color: "#6b7280" }}>
              The institution or company your work is billed to, and its address. The VAT id is
              optional. Once the facility confirms your details, only its staff change them.
            </p>
            <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
              {BILLING_LINES.map(([key, label]) => (
                <Field key={key} label={label} required={!OPTIONAL_BILLING.has(key)}>
                  <Input value={form[key]} onChange={set(key)}
                         placeholder={key === "vat_id" ? "ATU…" : undefined} />
                </Field>
              ))}
            </div>
          </section>
        )}

        {needsAuth && (
          <Field label="Your current password"
                 hint="Asked because this change alters how you sign in: an e-mail address is where a password-reset link goes.">
            <PasswordInput value={form.current_password} onChange={set("current_password")}
                           placeholder="••••••••" />
          </Field>
        )}

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Btn type="submit" disabled={!canSave}>{busy ? "Saving…" : "Save changes"}</Btn>
          {user?.profile_state === "incomplete" && (
            <Btn variant="secondary" onClick={submitProfile}
                 disabled={busy || !passwordOk || (needsAuth && !form.current_password)}>
              Submit for confirmation
            </Btn>
          )}
        </div>
      </form>

      {/* A staff account's billing identity is its group's, read-only; an
          administrator has none and a plain user's is not shown on this card. */}
      {billedAsGroup(user?.role) && (
        <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
          <GroupBillingBlock account={user} groupName={user?.billing_group_name} />
        </div>
      )}

      {/* Stated rather than absent. A field that is simply missing reads as a
          rendering fault; a field that says who changes it is an answer. */}
      <div style={{ marginTop: 22, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Set by an administrator
        </div>
        <dl style={{ margin: "10px 0 0", display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", fontSize: 13 }}>
          <dt style={{ color: "#6b7280" }}>Roles</dt>
          <dd style={{ margin: 0 }}>
            {grants.length === 0
              ? "No roles"
              : grants.map(g => `${ROLE_LABELS[g.role] || g.role}${g.group_name ? ` in ${g.group_name}` : ""}`).join(", ")}
          </dd>
          <dt style={{ color: "#6b7280" }}>Group</dt>
          <dd style={{ margin: 0 }}>{user?.group_name || "—"}</dd>
        </dl>
        <p style={{ margin: "10px 0 0", fontSize: 12, color: "#6b7280" }}>
          An administrator changes these.
        </p>
      </div>
    </Card>
  );
}
